import { TopicPageProps } from '../topic-page/types';
import {
  Container,
  TabsContentContainer,
  TabsList,
  TabsRootContainer,
} from '@socialchorus/shared-ui-components';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { getChannelById } from '../../../models/channels/selectors';
import styles from './styles.module.scss';
import { useParams } from 'react-router';
import { ChannelTab, ChannelTabType } from '../../../models/channels/types';
import MembersTab from './tabs/MembersTab';
import ShortcutsTab from './tabs/ShortcutsTab';
import PostsTab from './tabs/PostsTab';
import AboutTab from './tabs/AboutTab';
import CustomPageTab from './tabs/CustomPageTab/CustomPageTab';
import FollowButton from './common/FollowButton';
import { useTabNavigation } from './hooks/useTabNavigation';

function TopicPageV2({ analyticsData }: TopicPageProps): JSX.Element {
  const {
    channelId: topicId,
    tabType: urlTabType,
    shortcutId,
  } = useParams<{
    channelId: string;
    tabType?: string;
    shortcutId?: string;
  }>();

  // If we're on a shortcut deeplink URL, treat it as shortcuts tab
  const activeTabType = shortcutId ? ChannelTabType.Shortcuts : urlTabType;

  const { t } = useTranslation();
  const topic = usePatronSelector((state) => getChannelById(state, topicId));

  const { tabToUrl, navigateToTab, activeTabId } = useTabNavigation({
    topicId,
    activeTabType,
    tabs: topic?.landing_page_tabs || [],
  });

  const renderTab = (tab: ChannelTab) => {
    switch (tab.tab_type) {
      case ChannelTabType.Custom:
        return (
          <CustomPageTab
            topicId={topicId}
            customPageId={tab.custom_page_id!}
            activeTabId={activeTabId}
          />
        );
      case ChannelTabType.About:
        return <AboutTab topicId={topicId} activeTabId={activeTabId} />;
      case ChannelTabType.Posts:
        return (
          <PostsTab
            topicId={topicId}
            analyticsData={analyticsData}
            activeTabId={activeTabId}
          />
        );
      case ChannelTabType.Shortcuts:
        return (
          <ShortcutsTab
            topicId={topicId}
            activeTabId={activeTabId}
            shortcutId={shortcutId}
          />
        );
      case ChannelTabType.Members:
        return <MembersTab topicId={topicId} activeTabId={activeTabId} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.topicPage}>
      {topic ? (
        <TabsRootContainer value={activeTabType} onValueChange={navigateToTab}>
          <div className={styles.Container}>
            <Container
              className={classNames(
                styles.HeaderSection,
                !topic.use_cover_image && styles.HeaderSection__NoCover
              )}
              fullWidth
              shadow="extra-light"
            >
              {topic.use_cover_image && (
                <div className={styles.CoverImage__Container}>
                  {topic.cover_image_url ? (
                    <img
                      className={styles.CoverImage}
                      src={topic.cover_image_url}
                      alt="Cover"
                    />
                  ) : (
                    <div className={styles.CoverImageDefault} />
                  )}
                </div>
              )}
              <div className={styles.HeaderInfo__Container}>
                <Container
                  variant="border"
                  className={styles.TopicImage__Container}
                  shape="small"
                >
                  {topic.background_image_url ? (
                    <img
                      className={styles.TopicImage}
                      src={topic.background_image_url}
                      width={140}
                      height={140}
                      alt=""
                    />
                  ) : (
                    <div
                      className={styles.TopicImageDefault}
                      style={{ backgroundColor: topic.background_color }}
                    />
                  )}
                </Container>

                <div className={styles.HeaderTexts}>
                  <span className="text-secondary-heading">{topic.name}</span>

                  <span className="text-body-1 font-medium-light text-black-60">
                    {topic.description}
                  </span>
                </div>

                <div className={styles.HeaderActions}>
                  <FollowButton
                    topicId={topicId}
                    analyticsData={analyticsData}
                  />
                </div>
              </div>

              <div className={styles.HeaderTabs}>
                {topic.landing_page_tabs.length > 1 && (
                  <TabsList
                    tabs={topic.landing_page_tabs.map((tab) => ({
                      label:
                        tab.title && tab.title !== tab.tab_type
                          ? tab.title
                          : t(`channel_details.${tab.tab_type}`),
                      value: tabToUrl(tab),
                    }))}
                  />
                )}
              </div>
            </Container>

            {/* Render custom tabs dynamically */}
            {topic.landing_page_tabs.map((tab) => (
              <TabsContentContainer key={tab.id} value={tabToUrl(tab)}>
                {renderTab(tab)}
              </TabsContentContainer>
            ))}
          </div>
        </TabsRootContainer>
      ) : (
        'Loading...'
      )}
    </div>
  );
}

export default TopicPageV2;
